import type { InboxReclassifyResult, ReclassifyCategory } from '../../lib/inboxApi';

/**
 * The reclassify menu, shared by the email pane (EmailDetail) and the RFQ pane (RfqDetail), which
 * each carried their own copy of it.
 *
 * No "Inbox" (D43.11). It wiped the classification, which put the email under Quotations, Price
 * List and Confirmed Orders at once and parked its RFQ; the server refuses it too.
 *
 * Pure — no React, no API calls — so a test can import it without the page's dependencies.
 */
export const RECLASSIFY_OPTIONS: ReadonlyArray<{ value: ReclassifyCategory; label: string }> = [
  { value: 'rfq', label: 'RFQ' },
  { value: 'quotation', label: 'Quotations' },
  { value: 'price_list', label: 'Price List' },
  { value: 'confirmed_order', label: 'Confirmed Orders' },
  { value: 'others', label: 'Others' },
];

export function reclassifyCategoryLabel(category: string): string {
  return RECLASSIFY_OPTIONS.find((o) => o.value === category)?.label || category;
}

/** The categories among `labels` a conversation can be in, first-seen order, no repeats. */
export function reclassifyCategoriesOf(labels?: readonly string[] | null): ReclassifyCategory[] {
  const out: ReclassifyCategory[] = [];
  for (const raw of Array.isArray(labels) ? labels : []) {
    const match = RECLASSIFY_OPTIONS.find((o) => o.value === String(raw));
    if (match && !out.includes(match.value)) out.push(match.value);
  }
  return out;
}

/** What to tell the user after a successful move. */
export function reclassifyOutcomeMessage(result: InboxReclassifyResult): string {
  const moved = result.reclassified;
  const label = moved ? reclassifyCategoryLabel(moved.category) : '';
  let message = !moved
    ? 'The conversation was moved.'
    : moved.moved > 1
    ? `Moved the conversation (${moved.moved} emails from the client) to ${label}.`
    : `Moved to ${label}.`;

  const creation = result.rfqCreation;
  if (creation) {
    if (creation.code === 'INBOX_RFQ_NO_ATTACHMENT') {
      message += ' No new RFQ was created: nothing in the conversation could be read as one, or its file is already an RFQ.';
    } else if (creation.created > 0) {
      message += ` ${creation.created} RFQ${creation.created === 1 ? '' : 's'} created.`;
    }
  }
  return message;
}
